import type { ContentAST, EmbedReference, InlineContent } from './astTypes'

/** Inline placement can reference one text block, never a multi-block section. */
export function isInlineEmbedReference(value: unknown): value is EmbedReference {
  return isEmbedReference(value) && (value.kind === 'block' || value.kind === 'range')
}

/** Do not flatten lists, images, sections, or multiple paragraphs into inline prose. */
export function inlineEmbedContent(ast: ContentAST | undefined): InlineContent[] | undefined {
  const block = ast?.blocks.length === 1 ? ast.blocks[0] : undefined
  return block && (block.type === 'paragraph' || block.type === 'heading' || block.type === 'quote')
    ? block.content
    : undefined
}

/** Validate references at markdown, API, and clipboard boundaries. */
export function isEmbedReference(value: unknown): value is EmbedReference {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const source = value as Record<string, unknown>
  return (
    Object.keys(source).every((key) => ['filename', 'blockId', 'kind', 'rangeId'].includes(key)) &&
    typeof source.filename === 'string' &&
    source.filename.trim() === source.filename &&
    source.filename.length > 0 &&
    source.filename.length <= 255 &&
    source.filename !== '.' &&
    source.filename !== '..' &&
    !source.filename.includes('/') &&
    !source.filename.includes('\\') &&
    Array.from(source.filename).every((character) => {
      const code = character.charCodeAt(0)
      return code > 31 && code !== 127 && code !== 0x2028 && code !== 0x2029
    }) &&
    typeof source.blockId === 'string' &&
    /^[a-z0-9]{5}$/i.test(source.blockId) &&
    (source.kind === 'block' || source.kind === 'section' || source.kind === 'heading' || source.kind === 'range') &&
    (source.rangeId === undefined
      ? source.kind !== 'range'
      : typeof source.rangeId === 'string' && /^[a-z0-9-]{36}$/i.test(source.rangeId))
  )
}

/** Canonical JSON that cannot close its enclosing HTML comment. */
export function serializeEmbedReference(source: EmbedReference): string {
  if (!isEmbedReference(source)) {
    throw new Error('Invalid embed reference')
  }

  return JSON.stringify({
    filename: source.filename,
    blockId: source.blockId,
    kind: source.kind,
    ...(source.rangeId ? { rangeId: source.rangeId } : {}),
  }).replace(/[<>&]/g, (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`)
}
